import styled, { keyframes } from 'styled-components';
import { tableColors } from '../../styles/TableStyles';

export const Sidebar = styled.aside`
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${({ $mode }) => ($mode === 'expanded' ? '240px' : '64px')};
  z-index: 60;
  background: linear-gradient(180deg,
    var(--white-a25) 0%,
    var(--white-a20) 35%,
    var(--white-a20) 100%
  );
  border-right: 1px solid var(--white-a25);
  backdrop-filter: blur(14px);
  box-shadow: 0 10px 28px var(--shadow-a15);
  transition: width 220ms ease;
  display: flex;
  flex-direction: column;
`;

export const Rail = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 8px;
`;

export const LogoWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ $mode }) => ($mode === 'expanded' ? '14px 10px 6px' : '10px 8px 4px')};
  width: 100%;
`;

export const LogoImg = styled.img`
  display: block;
  max-width: 100%;
  width: ${({ $mode }) => ($mode === 'expanded' ? '160px' : '44px')};
  height: auto;
  filter: drop-shadow(0 2px 4px var(--shadow-a15));
  object-fit: contain;
`;

export const IconBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  width: 100%;
  border: none;
  background: transparent;
  color: ${tableColors.header};
  border-radius: 10px;
  cursor: pointer;
  transition: background 200ms ease;
  outline: none;
  position: relative;

  &[aria-current='page'] {
    background: var(--green-a15);
  }

  &:hover {
    background: var(--green-a08);
  }
  &:focus-visible {
    box-shadow: 0 0 0 2px var(--orange-a60);
  }

  &[aria-current='page']::before {
    content: '';
    position: absolute;
    left: 6px;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 18px;
    border-radius: 2px;
    background: ${tableColors.accent};
  }
`;

export const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  color: ${tableColors.header};
  border-radius: 10px;
  cursor: pointer;
  transition: background 200ms ease;
  &:hover { background: var(--green-a08); }
`;

export const Items = styled.div`
  display: grid;
  grid-auto-rows: minmax(32px, auto);
  padding: 4px 6px 8px 22px;
  gap: 4px;
`;

export const SubItem = styled.button`
  text-align: left;
  padding: 6px 8px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: ${tableColors.text};
  cursor: pointer;
  position: relative;
  &:hover { background: var(--green-a08); }
  &[aria-current='page'] {
    background: var(--green-a15);
  }
  &[aria-current='page']::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 18px;
    background: ${tableColors.accent};
    border-radius: 2px;
  }
`;

// Mobile drawer
const slideIn = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
`;
export const Drawer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 70;
  display: ${({ $open }) => ($open ? 'block' : 'none')};
  backdrop-filter: blur(2px);
  background: var(--shadow-a25);
  & > div {
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 64px;
    background: var(--white-a20);
    border-right: 1px solid var(--white-a25);
    animation: ${slideIn} 220ms ease;
    display: flex;
    flex-direction: column;
    padding-top: 52px; /* ensure first icon not under the X */
    padding-bottom: 8px;
  }
`;

export const Hamburger = styled.button`
  position: fixed;
  left: 12px; top: 12px;
  z-index: 80;
  display: none;
  width: 36px; height: 36px;
  align-items: center; justify-content: center;
  border: none; border-radius: 8px;
  background: var(--white-a40);
  backdrop-filter: blur(8px);
  box-shadow: 0 2px 10px var(--shadow-a08);
  cursor: pointer;
  color: ${tableColors.text};
  &:hover { box-shadow: 0 4px 14px var(--shadow-a15); }
  @media (max-width: 767px) { display: flex; }
`;


// Profile section at the bottom of the sidebar
export const ProfileWrap = styled.div`
  margin-top: auto;
  padding: 10px 8px ${({ $expanded }) => ($expanded ? '64px' : '14px')};
  transition: padding-bottom 160ms ease;
  position: relative;
`;

export const ProfileButton = styled.button`
  width: 100%;
  border: none;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0.5rem;
  border-radius: 12px;
  cursor: pointer;
  background: var(--green-800);
  color: var(--white);
  box-shadow: 0 6px 18px var(--shadow-a15);
  transition: transform 120ms ease, box-shadow 120ms ease, background 120ms ease;
  text-align: left;
  &:hover { transform: translateY(-1px); box-shadow: 0 10px 22px var(--shadow-a15); }
`;

export const Avatar = styled.div`
  width: 36px; height: 36px; border-radius: 0.3rem;
  display: inline-flex; align-items: center; justify-content: center;
  font-weight: 800; color: var(--cream-400); background:var(--shadow-a15);
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`;

export const ProfileText = styled.div`
  display: flex; flex-direction: column; min-width: 0;
  padding-right: 10px;
  strong { font-weight: 800; color: var(--white); line-height: 1.1; overflow-wrap: anywhere; }
  span { color: var(--cream-400); font-weight: 300; opacity: 0.95; font-size: 0.75rem; overflow-wrap: anywhere; }
`;

export const LogoutInline = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: calc(100% - 16px);
  position: absolute;
  left: 8px;
  bottom: 10px;
  padding: 10px 12px;
  border: 1px solid var(--white-a40);
  background: var(--white-a25);
  backdrop-filter: blur(8px);
  color: var(--green-800);
  border-radius: 10px;
  cursor: pointer;
  transition: background 140ms ease, transform 120ms ease, box-shadow 140ms ease;
  &:hover { background: var(--white-a40); box-shadow: 0 8px 18px var(--shadow-a15); }
  &:active { transform: translateY(1px); }
`;


